/**
 * Application-wide error codes for routing and diagnostics.
 * User-facing titles and explanations: {@link ./error_user_messages.ts}.
 * Each code maps to a dedicated error view (see code_to_error_route.ts).
 *
 * - `BACKEND_*`: must match the BFF `ErrorCode` StrEnum string values exactly.
 * - `CLIENT_*`: determined only in the SPA (OAuth parsing, health gate, routing).
 */
export enum ErrorCode {
  /** Generic 401 from the API or network path treated as auth failure; optional `error_message` from BFF. */
  Unauthorized = 'CLIENT_40101',
  /** BFF: token exchange failed after OAuth callback (`ErrorCode.AUTH_CODE_TOKEN_EXCHANGE_FAILED`). */
  AuthCodeTokenExchangeFailed = 'BACKEND_10001',
  /** BFF: JWT signature verification failed for tokens from Keycloak (`KEYCLOAK_TOKEN_SIGNATURE_INVALID`). */
  KeycloakTokenSignatureInvalid = 'BACKEND_10002',
  /** BFF: JWT expiration validation failed (`KEYCLOAK_TOKEN_EXPIRATION_INVALID`). */
  KeycloakTokenExpirationInvalid = 'BACKEND_10003',
  /** BFF: JWT decode failed after auth-code exchange (`KEYCLOAK_JWT_DECODE_FAILED`). */
  KeycloakJwtDecodeFailed = 'BACKEND_10004',
  /** BFF: token claim validation failed after decode (`UNKNOWN_ERROR_DURING_JWT_DECODING`). */
  UnknownErrorDuringJwtDecoding = 'BACKEND_10005',
  /** BFF: ID token nonce does not match client nonce (`KEYCLOAK_ID_TOKEN_NONCE_VIOLATION`). */
  KeycloakIdTokenNonceViolation = 'BACKEND_10006',
  /** BFF: Keycloak token introspection failed (`KEYCLOAK_INTROSPECTION_FAILED`). */
  KeycloakIntrospectionFailed = 'BACKEND_10007',
  /** BFF: encrypted session cookie payload exceeded size limit (`XAUTH_DATA_COOKIE_TOO_LARGE`). */
  XauthDataCookieTooLarge = 'BACKEND_10008',
  /** BFF: UserInfo request timed out after token exchange (`KEYCLOAK_USERINFO_TIMEOUT`). */
  KeycloakUserinfoTimeout = 'BACKEND_10009',
  /** BFF: UserInfo returned non-success HTTP after Bearer token (`KEYCLOAK_USERINFO_HTTP_ERROR`). */
  KeycloakUserinfoHttpError = 'BACKEND_10010',
  /** BFF: unexpected UserInfo failure (not timeout or HTTP error) (`KEYCLOAK_USERINFO_UNKNOWN_ERROR`). */
  KeycloakUserinfoUnknownError = 'BACKEND_10011',
  /** BFF: UserInfo omitted email and phone; backend requires at least one contact (`USERINFO_MISSING_CONTACT`). */
  UserinfoMissingContact = 'BACKEND_10012',
  /** BFF: UserInfo omitted required first/last name (`USERINFO_MISSING_NAME`). */
  UserinfoMissingName = 'BACKEND_10013',
  /** BFF: UserInfo omitted required gender (`USERINFO_MISSING_GENDER`). */
  UserinfoMissingGender = 'BACKEND_10014',
  /** BFF: UserInfo gender not a supported value (`USERINFO_INVALID_GENDER`). */
  UserinfoInvalidGender = 'BACKEND_10015',
  /** BFF: UserInfo omitted required address (`USERINFO_MISSING_ADDRESS`). */
  UserinfoMissingAddress = 'BACKEND_10016',
  /** BFF: UserInfo JSON failed validation (generic / unmapped) (`USERINFO_VALIDATION`). */
  UserinfoValidation = 'BACKEND_10017',
  /** BFF: UserInfo `groups` contains a path not in the app Group enum (`USERINFO_UNKNOWN_GROUP`). */
  UserinfoUnknownGroup = 'BACKEND_10018',
  /** BFF: UserInfo `roles` contains a name not in ResourceAccessRole (`USERINFO_UNKNOWN_ROLE`). */
  UserinfoUnknownRole = 'BACKEND_10019',
  /** BFF: protected request without `XSRF-TOKEN` cookie (`XSRF_TOKEN_COOKIE_MISSING`). */
  XsrfTokenCookieMissing = 'BACKEND_10020',
  /** BFF: token cookie present but `X-CSRF-TOKEN` header missing (`NO_CSRF_HEADER`). */
  NoCsrfHeader = 'BACKEND_10021',
  /** BFF: `X-CSRF-TOKEN` and `XSRF-TOKEN` cookie both sent but values differ (`CSRF_TOKEN_MISMATCH`). */
  CsrfTokenMismatch = 'BACKEND_10022',
  /** BFF: `XAUTH-DATA` cookie cannot be base64url-decoded or AES-256-GCM decrypted (`XAUTH_DATA_COOKIE_DECRYPT_FAILED`). */
  XauthDataCookieDecryptFailed = 'BACKEND_10023',
  /** BFF: decrypted `XAUTH-DATA` payload cannot be decompressed as LZ4 frame (`XAUTH_DATA_COOKIE_DECOMPRESS_FAILED`). */
  XauthDataCookieDecompressFailed = 'BACKEND_10024',
  /** BFF: decompressed `XAUTH-DATA` payload cannot be deserialized into expected token schema (`XAUTH_DATA_COOKIE_DESERIALIZE_FAILED`). */
  XauthDataCookieDeserializeFailed = 'BACKEND_10025',
  /** BFF: unexpected runtime error while processing `XAUTH-DATA` after decode/decrypt started and before a valid token bundle is loaded (`XAUTH_DATA_COOKIE_UNKNOWN_ERROR`). */
  XauthDataCookieUnknownError = 'BACKEND_10026',
  /** BFF: protected request but no `AccessTokenPayload` in request state (`XAUTH-DATA` absent or not parseable) (`ACCESS_TOKEN_MISSING`). */
  AccessTokenMissing = 'BACKEND_10027',
  /** BFF: authenticated user lacks required `ResourceAccessRole` permissions for the endpoint (`PERMISSION_DENIED`). */
  PermissionDenied = 'BACKEND_10028',
  /** BFF: `POST /persons/clients/by-ids` found no person with the `client` role for any of the supplied ids (ids unknown, or their persons lack the `client` role); returned as HTTP 403 by the BFF (`CLIENTS_BY_IDS_NO_CLIENTS_FOUND`). */
  ClientsByIdsNoClientsFound = 'BACKEND_10029',
  /** BFF: `GET /auth/me` (or another session-only endpoint) called without an `XAUTH-DATA` session cookie attached to the request, so no authenticated user can be resolved; the client must re-authenticate (`SESSION_NOT_AUTHENTICATED`). */
  SessionNotAuthenticated = 'BACKEND_10030',
  /** BFF: the `XAUTH-DATA` cookie carried an expired access token and the middleware attempt to exchange the stored refresh token for a new access token against the Keycloak token endpoint failed (refresh token expired/invalidated, Keycloak HTTP error, or transport failure after retries). The session has ended; cookies are cleared and the client must re-authenticate (`ACCESS_TOKEN_REFRESH_FAILED`). */
  AccessTokenRefreshFailed = 'BACKEND_10031',
  /** OAuth callback URL had no fragment or query with `code` / `state` (nothing to parse). */
  NoFragmentInAuthResponse = 'CLIENT_40102',
  /** OAuth callback params present but `code` is missing. */
  NoCodeInAuthResponse = 'CLIENT_40103',
  /** OAuth callback params present but `state` is missing. */
  NoStateInAuthResponse = 'CLIENT_40104',
  /** OAuth `state` does not match the value stored at login start (CSRF / session binding). */
  InvalidStateInAuthResponse = 'CLIENT_40105',
  /** OAuth callback missing `session_state` from Keycloak. */
  NoSessionStateInAuthResponse = 'CLIENT_40106',
  /** OAuth callback missing `iss` (issuer) parameter. */
  NoIssInAuthResponse = 'CLIENT_40107',
  /** OAuth `iss` does not match the configured Keycloak issuer. */
  InvalidIssInAuthResponse = 'CLIENT_40108',
  /** Expected OAuth nonce missing from session storage before token exchange. */
  NoNonceInStorage = 'CLIENT_40109',
  /** Unknown route or catch-all redirect; original path may appear in query. */
  NotFound = 'CLIENT_40401',
  /** Keycloak health check not UP before starting login redirect. */
  KeycloakUnavailable = 'CLIENT_50301',
}
